'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { PlatformStats, KycItem, WorkspaceItem, UserItem } from '../types';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { LoginCard } from '../components/LoginCard';
import { OverviewTab } from '../components/OverviewTab';
import { KycTab } from '../components/KycTab';
import { WorkspacesTab } from '../components/WorkspacesTab';
import { UsersTab } from '../components/UsersTab';
import { AuditLogTab } from '../components/AuditLogTab';
import { ChangePasswordModal } from '../components/Modals/ChangePasswordModal';
import { DeleteUserModal } from '../components/Modals/DeleteUserModal';
import { DocumentPreviewModal } from '../components/Modals/DocumentPreviewModal';
import { InspectWorkspaceModal } from '../components/Modals/InspectWorkspaceModal';
import { InspectBoardModal } from '../components/Modals/InspectBoardModal';
import { InspectCardModal } from '../components/Modals/InspectCardModal';
import { InspectUserModal } from '../components/Modals/InspectUserModal';
import { InspectAuditLogModal } from '../components/Modals/InspectAuditLogModal';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

export default function SuperadminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'KYC' | 'WORKSPACES' | 'USERS' | 'AUDIT_LOG'>('OVERVIEW');
  const [userFilter, setUserFilter] = useState<'ALL' | 'ORGANIZATION' | 'MEMBER' | 'SUSPENDED'>('ALL');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Audit Logs State
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState<string>('');
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [allWorkspacesDropdown, setAllWorkspacesDropdown] = useState<any[]>([]);

  // Toast Notification State
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Delete User Custom Modal State
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string; email: string } | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  // Change Password Modal State
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Drill-down Inspection Modal States
  const [inspectWorkspaceId, setInspectWorkspaceId] = useState<string | null>(null);
  const [inspectWorkspaceData, setInspectWorkspaceData] = useState<{ workspace: any; totalBoards: number; boards: any[] } | null>(null);
  const [loadingBoards, setLoadingBoards] = useState(false);

  const [inspectBoardId, setInspectBoardId] = useState<string | null>(null);
  const [inspectBoardData, setInspectBoardData] = useState<{ board: any; totalCards: number; lists: any[] } | null>(null);
  const [loadingCards, setLoadingCards] = useState(false);

  const [inspectCardId, setInspectCardId] = useState<string | null>(null);
  const [inspectCardData, setInspectCardData] = useState<any | null>(null);
  const [loadingCardDetail, setLoadingCardDetail] = useState(false);

  const [inspectUser, setInspectUser] = useState<UserItem | null>(null);
  const [inspectLog, setInspectLog] = useState<any | null>(null);

  // Search & Backend Pagination States
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [wsPage, setWsPage] = useState(1);
  const [wsTotal, setWsTotal] = useState(0);
  const [wsTotalPages, setWsTotalPages] = useState(1);

  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const pageSize = 8;

  // Data States
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [kycList, setKycList] = useState<KycItem[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState<{ [id: string]: string }>({});
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [selectedDocUrl, setSelectedDocUrl] = useState<string | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleOpenWorkspaceBoards = async (wsId: string) => {
    setInspectWorkspaceId(wsId);
    setInspectWorkspaceData(null);
    setLoadingBoards(true);
    try {
      const res = await api.get(`/superadmin/workspaces/${wsId}/boards`);
      if (res.data?.data) {
        setInspectWorkspaceData(res.data.data);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to fetch workspace boards.');
      setInspectWorkspaceId(null);
    } finally {
      setLoadingBoards(false);
    }
  };

  const handleOpenBoardCards = async (boardId: string) => {
    setInspectBoardId(boardId);
    setInspectBoardData(null);
    setLoadingCards(true);
    try {
      const res = await api.get(`/superadmin/boards/${boardId}/cards`);
      if (res.data?.data) {
        setInspectBoardData(res.data.data);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to fetch board cards.');
      setInspectBoardId(null);
    } finally {
      setLoadingCards(false);
    }
  };

  const handleOpenCardDetail = async (cardId: string) => {
    setInspectCardId(cardId);
    setInspectCardData(null);
    setLoadingCardDetail(true);
    try {
      const res = await api.get(`/superadmin/cards/${cardId}`);
      if (res.data?.data) {
        setInspectCardData(res.data.data);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to fetch card details.');
      setInspectCardId(null);
    } finally {
      setLoadingCardDetail(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setWsPage(1);
      setUsersPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const token = localStorage.getItem('scalesutra_access_token');
    if (token) {
      setAuthenticated(true);
      loadStatsAndKyc();
    } else {
      setLoading(false);
    }
  }, []);

  // Background token refresh
  useEffect(() => {
    if (!authenticated) return;
    const silentRefresh = async () => {
      const refreshToken = localStorage.getItem('scalesutra_refresh_token');
      if (refreshToken) {
        try {
          const res = await api.post('/auth/refresh', { refreshToken });
          if (res.data?.data?.access_token) {
            localStorage.setItem('scalesutra_access_token', res.data.data.access_token);
            if (res.data?.data?.refresh_token) {
              localStorage.setItem('scalesutra_refresh_token', res.data.data.refresh_token);
            }
          }
        } catch (err) {
          console.warn('[Silent Auto-Refresh] Token refresh warning:', err);
        }
      }
    };

    const interval = setInterval(silentRefresh, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authenticated]);

  const loadStatsAndKyc = async () => {
    setLoading(true);
    try {
      const [statsRes, kycRes] = await Promise.all([
        api.get('/superadmin/stats').catch(() => ({ data: { data: null } })),
        api.get('/kyc/pending').catch(() => ({ data: { data: [] } })),
      ]);
      if (statsRes.data?.data) setStats(statsRes.data.data);
      if (kycRes.data?.data) setKycList(kycRes.data.data);
    } catch (err) {
      console.error('Failed to load stats/kyc', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/workspaces', {
        params: {
          page: wsPage,
          limit: pageSize,
          search: debouncedSearch,
        },
      });
      if (res.data?.data) {
        setWorkspaces(res.data.data.workspaces || []);
        setWsTotal(res.data.data.total || 0);
        setWsTotalPages(res.data.data.totalPages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces', err);
    } finally {
      setLoading(false);
    }
  }, [wsPage, debouncedSearch]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/superadmin/users', {
        params: {
          page: usersPage,
          limit: pageSize,
          search: debouncedSearch,
          filter: userFilter,
        },
      });
      if (res.data?.data) {
        setUsers(res.data.data.users || []);
        setUsersTotal(res.data.data.total || 0);
        setUsersTotalPages(res.data.data.totalPages || 1);
      }
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setLoading(false);
    }
  }, [usersPage, debouncedSearch, userFilter]);

  const fetchAllWorkspacesForDropdown = useCallback(async () => {
    try {
      const res = await api.get('/superadmin/workspaces', {
        params: {
          page: 1,
          limit: 100,
        },
      });
      if (res.data?.data?.workspaces) {
        setAllWorkspacesDropdown(res.data.data.workspaces);
        if (!selectedWorkspaceId) {
          setSelectedWorkspaceId('__GLOBAL__');
          setSelectedWorkspaceName('Global Platform Events');
        }
      }
    } catch (err) {
      console.error('Failed to load workspaces list', err);
    }
  }, [selectedWorkspaceId]);

  const fetchAuditLogs = async (wsId: string) => {
    setLoadingAuditLogs(true);
    try {
      const endpoint = wsId === '__GLOBAL__' 
        ? '/superadmin/activities' 
        : `/superadmin/activities?workspaceId=${wsId}`;
      const res = await api.get(endpoint);
      if (res.data?.data) {
        const logs = Array.isArray(res.data.data) ? res.data.data : (res.data.data.items || []);
        setAuditLogs(logs);
      }
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to fetch audit logs.');
    } finally {
      setLoadingAuditLogs(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      if (activeTab === 'WORKSPACES') fetchWorkspaces();
      if (activeTab === 'USERS') fetchUsers();
      if (activeTab === 'AUDIT_LOG') fetchAllWorkspacesForDropdown();
    }
  }, [authenticated, activeTab, fetchWorkspaces, fetchUsers, fetchAllWorkspacesForDropdown]);

  useEffect(() => {
    if (authenticated && activeTab === 'AUDIT_LOG' && selectedWorkspaceId) {
      fetchAuditLogs(selectedWorkspaceId);
    }
  }, [authenticated, activeTab, selectedWorkspaceId]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const res = await api.post('/auth/login', {
        email: loginEmail,
        password: loginPassword,
      });

      if (res.data?.data?.access_token) {
        localStorage.setItem('scalesutra_access_token', res.data.data.access_token);
        if (res.data?.data?.refresh_token) {
          localStorage.setItem('scalesutra_refresh_token', res.data.data.refresh_token);
        }
        setAuthenticated(true);
        loadStatsAndKyc();
        showToast('success', 'Authenticated as Superadmin successfully.');
      }
    } catch (err: any) {
      setLoginError(err?.response?.data?.message || 'Login failed. Invalid Superadmin credentials.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleReviewKyc = async (kycId: string, status: 'APPROVED' | 'REJECTED') => {
    setSubmittingId(kycId);
    try {
      await api.patch(`/kyc/${kycId}/review`, {
        status,
        rejectionReason: rejectionReason[kycId] || undefined,
      });
      setKycList((prev) => prev.filter((item) => item.id !== kycId));
      loadStatsAndKyc();
      showToast('success', `KYC application ${status.toLowerCase()} successfully.`);
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to update KYC status.');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      await api.patch(`/superadmin/users/${userId}/status`, { accountStatus: newStatus });
      fetchUsers();
      showToast('success', `User account status updated to ${newStatus}.`);
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to update user status.');
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUserId(userToDelete.id);
    try {
      await api.delete(`/superadmin/users/${userToDelete.id}`);
      showToast('success', `User account for ${userToDelete.name} deleted successfully.`);
      fetchUsers();
      loadStatsAndKyc();
      setUserToDelete(null);
    } catch (err: any) {
      showToast('error', err?.response?.data?.message || 'Failed to delete user account.');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      showToast('success', res.data?.data?.message || 'Password changed successfully!');
      setPasswordSuccess('Password changed successfully!');
      setTimeout(() => {
        setChangePasswordModalOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSuccess('');
      }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || 'Failed to change password.';
      setPasswordError(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'WORKSPACES') fetchWorkspaces();
    else if (activeTab === 'USERS') fetchUsers();
    else if (activeTab === 'AUDIT_LOG' && selectedWorkspaceId) fetchAuditLogs(selectedWorkspaceId);
    else loadStatsAndKyc();
  };

  if (!authenticated) {
    return (
      <LoginCard
        loginEmail={loginEmail}
        setLoginEmail={setLoginEmail}
        loginPassword={loginPassword}
        setLoginPassword={setLoginPassword}
        loginError={loginError}
        loginLoading={loginLoading}
        handleLogin={handleLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#060811] text-slate-100 font-sans flex antialiased selection:bg-indigo-500 selection:text-white relative">
      {/* Dynamic Toast Notification Banner */}
      {toast && (
        <div className="fixed top-5 right-5 z-[100] transition-all animate-bounce">
          <div
            className={`px-5 py-3 rounded-2xl shadow-2xl backdrop-blur-xl border text-xs font-bold flex items-center gap-2.5 ${
              toast.type === 'success'
                ? 'bg-emerald-950/90 border-emerald-500/40 text-emerald-300'
                : 'bg-rose-950/90 border-rose-500/40 text-rose-300'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0" />
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Sidebar Component */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        kycCount={kycList.length}
        wsTotal={wsTotal}
        usersTotal={usersTotal}
        setUserFilter={setUserFilter}
        setUsersPage={setUsersPage}
        onOpenChangePassword={() => {
          setPasswordError('');
          setPasswordSuccess('');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          setChangePasswordModalOpen(true);
        }}
        onSignOut={() => {
          localStorage.removeItem('scalesutra_access_token');
          localStorage.removeItem('scalesutra_refresh_token');
          setAuthenticated(false);
        }}
      />

      {/* Main Content Body */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header Navbar Component */}
        <Header
          activeTab={activeTab}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          setMobileMenuOpen={setMobileMenuOpen}
          onRefresh={handleRefresh}
          loading={loading}
        />

        {/* Tab Content Area */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-8">
          {activeTab === 'OVERVIEW' && (
            <OverviewTab
              stats={stats}
              kycList={kycList}
              setActiveTab={setActiveTab}
              setUserFilter={setUserFilter}
              setUsersPage={setUsersPage}
              setWsPage={setWsPage}
            />
          )}

          {activeTab === 'KYC' && (
            <KycTab
              kycList={kycList}
              rejectionReason={rejectionReason}
              setRejectionReason={setRejectionReason}
              submittingId={submittingId}
              handleReviewKyc={handleReviewKyc}
              setSelectedDocUrl={setSelectedDocUrl}
            />
          )}

          {activeTab === 'WORKSPACES' && (
            <WorkspacesTab
              workspaces={workspaces}
              wsTotal={wsTotal}
              wsPage={wsPage}
              setWsPage={setWsPage}
              wsTotalPages={wsTotalPages}
              pageSize={pageSize}
              loading={loading}
              searchTerm={searchTerm}
              handleOpenWorkspaceBoards={handleOpenWorkspaceBoards}
              setSelectedWorkspaceId={setSelectedWorkspaceId}
              setSelectedWorkspaceName={setSelectedWorkspaceName}
              setActiveTab={setActiveTab}
            />
          )}

          {activeTab === 'USERS' && (
            <UsersTab
              users={users}
              userFilter={userFilter}
              setUserFilter={setUserFilter}
              usersTotal={usersTotal}
              usersPage={usersPage}
              setUsersPage={setUsersPage}
              usersTotalPages={usersTotalPages}
              pageSize={pageSize}
              loading={loading}
              searchTerm={searchTerm}
              handleToggleUserStatus={handleToggleUserStatus}
              setUserToDelete={setUserToDelete}
              handleOpenWorkspaceBoards={handleOpenWorkspaceBoards}
              onSelectUser={(u) => setInspectUser(u)}
            />
          )}

          {activeTab === 'AUDIT_LOG' && (
            <AuditLogTab
              selectedWorkspaceId={selectedWorkspaceId}
              setSelectedWorkspaceId={setSelectedWorkspaceId}
              selectedWorkspaceName={selectedWorkspaceName}
              setSelectedWorkspaceName={setSelectedWorkspaceName}
              allWorkspacesDropdown={allWorkspacesDropdown}
              auditLogs={auditLogs}
              loadingAuditLogs={loadingAuditLogs}
              fetchAuditLogs={fetchAuditLogs}
              handleOpenWorkspaceBoards={handleOpenWorkspaceBoards}
              handleOpenBoardCards={handleOpenBoardCards}
              handleOpenCardDetail={handleOpenCardDetail}
              onSelectLog={(log) => setInspectLog(log)}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <InspectAuditLogModal
        log={inspectLog}
        onClose={() => setInspectLog(null)}
        handleOpenWorkspaceBoards={handleOpenWorkspaceBoards}
        handleOpenBoardCards={handleOpenBoardCards}
        handleOpenCardDetail={handleOpenCardDetail}
      />

      <InspectUserModal
        user={inspectUser}
        onClose={() => setInspectUser(null)}
        handleToggleUserStatus={handleToggleUserStatus}
        setUserToDelete={setUserToDelete}
        handleOpenWorkspaceBoards={handleOpenWorkspaceBoards}
      />

      <InspectWorkspaceModal
        inspectWorkspaceId={inspectWorkspaceId}
        inspectWorkspaceData={inspectWorkspaceData}
        loadingBoards={loadingBoards}
        onClose={() => {
          setInspectWorkspaceId(null);
          setInspectWorkspaceData(null);
        }}
        handleOpenBoardCards={handleOpenBoardCards}
      />

      <InspectBoardModal
        inspectBoardId={inspectBoardId}
        inspectBoardData={inspectBoardData}
        loadingCards={loadingCards}
        onClose={() => {
          setInspectBoardId(null);
          setInspectBoardData(null);
        }}
        handleOpenCardDetail={handleOpenCardDetail}
      />

      <InspectCardModal
        inspectCardId={inspectCardId}
        inspectCardData={inspectCardData}
        loadingCardDetail={loadingCardDetail}
        onClose={() => {
          setInspectCardId(null);
          setInspectCardData(null);
        }}
      />

      <DeleteUserModal
        userToDelete={userToDelete}
        onClose={() => setUserToDelete(null)}
        confirmDeleteUser={confirmDeleteUser}
        deletingUserId={deletingUserId}
      />

      <DocumentPreviewModal
        selectedDocUrl={selectedDocUrl}
        onClose={() => setSelectedDocUrl(null)}
      />

      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
        currentPassword={currentPassword}
        setCurrentPassword={setCurrentPassword}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        passwordLoading={passwordLoading}
        passwordError={passwordError}
        passwordSuccess={passwordSuccess}
        onSubmit={handleChangePassword}
      />
    </div>
  );
}
